import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { DxTradeOrdersResponse } from '@model/DxTrade.types'

import { getPortfolioStoreSnapshot, subscribeToPortfolioStore } from './useDxtradeAccountWebSocket'

export const useDxtradeOrders = (
  accountCode: string
): {
  data: DxTradeOrdersResponse | undefined
  isLoading: boolean
  forceRefresh: (skipLoadingFlag?: boolean) => void
} => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const subscribe = useCallback(
    (listener: () => void) => subscribeToPortfolioStore(accountCode, listener),
    [accountCode]
  )
  const wsPortfolio = useSyncExternalStore(subscribe, () => getPortfolioStoreSnapshot(accountCode))

  const { data: restData, isLoading: restIsLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.orders(accountCode),
    queryFn: () => apiClient.getDxtradeOrders(accountCode),
    enabled: !!accountCode,
  })

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.trade.dxtrade.orders(accountCode),
    })
  }, [queryClient, accountCode])

  const data = useMemo<DxTradeOrdersResponse | undefined>(() => {
    if (wsPortfolio) {
      return { orders: wsPortfolio.orders, nextPageTransactionTime: null }
    }
    return restData
  }, [wsPortfolio, restData])

  const isLoading = wsPortfolio ? false : restIsLoading

  return { data, isLoading, forceRefresh }
}
